import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Eye, FilterX } from "lucide-react";
import { Button } from "@shared/ui/button";
import { PageHeader } from "@shared/components/PageHeader";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Checkbox } from "@shared/ui/checkbox";
import { Badge } from "@shared/ui/badge";
import { MoveRoutesDialog } from "./movePremiseModal";
import {
  usePremise,
  useRoutePrimise,
} from "@features/mx/routeManagement/hooks";
import {
  useGetArea,
  useGetSubArea,
} from "@features/mx/readCycleManagement/hooks";

interface RouteData {
  id: string;
  routeName: string;
  readType: string;
  utilityService: string;
  meters: number;
  premises: number;
}

interface RouteFilters {
  area: string;
  subArea: string;
}

export const PremisesTab = ({ routeDetails }: { routeDetails: any }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Extract filters from URL
  const currentPage = Number(searchParams.get("page") || "1");
  const currentSearch = decodeURIComponent(
    searchParams.get("search_data") || "",
  );
  const currentArea = searchParams.get("area") || "all";
  const currentSubArea = searchParams.get("sub_area") || "all";

  const [pendingFilters, setPendingFilters] = useState({
    area: currentArea,
    subArea: currentSubArea,
  });

  // Fetch dropdown data
  const { data: areaData } = useGetArea();
  const { data: subAreaData } = useGetSubArea(
    currentArea !== "all" ? [currentArea] : [],
  );

  const { data: premiseData, isLoading } = useRoutePrimise(
    routeDetails?.premise || [],
    currentSearch || undefined,
    currentPage,
    PAGE_SIZE,
    currentArea !== "all" ? currentArea : undefined, // Pass area filter
    currentSubArea !== "all" ? currentSubArea : undefined, // Pass subArea filter
  );

  // Add state for selected routes
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  // State for move routes dialog
  const [moveRoutesDialogOpen, setMoveRoutesDialogOpen] = useState(false);

  // Mock data
  const premiseDetail = (premiseData?.result || []).map((item, index) => ({
    id: item.hierarchyCodes?.premise,
    name: item.hierarchyNames?.premise || item?.name || "NA",
    area: item.hierarchyNames?.area || "NA",
    subArea: item.hierarchyNames?.subArea || "NA",
    meters: item.installedMeterCount || 0,
    consumers: item.totalConsumerCount || 0,
  }));

  // Real dropdown options from API
  const areaOptions = useMemo(
    () =>
      areaData?.map((area) => ({
        value: String(area.code),
        label: area.name,
      })) || [],
    [areaData],
  );

  const subAreaOptions = useMemo(
    () =>
      subAreaData?.map((subArea) => ({
        value: String(subArea.code),
        label: subArea.name,
      })) || [],
    [subAreaData],
  );

  // Calculate all selected outside of render to avoid recalculations
  const allSelected = useMemo(() => {
    return (
      selectedRoutes.length === premiseDetail.length && premiseDetail.length > 0
    );
  }, [selectedRoutes.length, premiseDetail.length]);

  // Calculate total meters for selected routes
  const estimatedMeters = useMemo(() => {
    return selectedRoutes.reduce((total, routeId) => {
      const route = premiseDetail.find((r) => r.id === routeId);
      return total + (route ? route.meters : 0);
    }, 0);
  }, [selectedRoutes, premiseDetail]);

  // Handle route selection
  const handleSelectRoute = (id: string, checked: boolean) => {
    setSelectedRoutes((prev) => {
      if (checked) {
        if (prev.includes(id)) return prev;
        return [...prev, id];
      } else {
        if (!prev.includes(id)) return prev;
        return prev.filter((routeId) => routeId !== id);
      }
    });
  };

  // Handle select all
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      if (selectedRoutes.length !== premiseDetail.length) {
        setSelectedRoutes(premiseDetail.map((route) => route.id));
      }
    } else {
      if (selectedRoutes.length > 0) {
        setSelectedRoutes([]);
      }
    }
  };

  // Handle opening move routes dialog
  const handleOpenMoveRoutesDialog = () => {
    setMoveRoutesDialogOpen(true);
  };

  // Handle move routes action
  const handleMoveRoutes = (destinationCycleId: string) => {
    console.log(
      "Moving routes:",
      selectedRoutes,
      "to cycle:",
      destinationCycleId,
    );
    setSelectedRoutes([]);
  };

  // Pagination state
  const pagination = useMemo(() => {
    const totalItems = premiseData?.count || premiseDetail.length;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: totalItems > 0 ? startItem : 0,
      endItem: totalItems > 0 ? endItem : 0,
    };
  }, [premiseData?.count, premiseDetail.length, currentPage]);

  const handlePendingFilterChange = (
    key: keyof typeof pendingFilters,
    value: string,
  ) => {
    setPendingFilters((prev) => {
      // If area changes, reset sub_area
      if (key === "area") {
        return {
          ...prev,
          [key]: value,
          subArea: "all",
        };
      }
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const handleApplyFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      area: pendingFilters.area,
      sub_area: pendingFilters.subArea,
      page: 1,
    };
    setSearchParams(newParams);
  };

  const handleResetFilters = () => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
    };
    delete newParams.area;
    delete newParams.sub_area;
    delete newParams.search_data;
    newParams.page = 1;
    setSearchParams(newParams);
    setPendingFilters({
      area: "all",
      subArea: "all",
    });
  };

  const handleSearch = (searchTerm: string) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      area: currentArea,
      sub_area: currentSubArea,
    };
    if (searchTerm.trim()) newParams.search_data = searchTerm.trim();
    else delete newParams.search_data;
    setSearchParams(newParams);
  };

  const handlePageChange = (page: number) => {
    const newParams: any = {
      ...Object.fromEntries(searchParams.entries()),
      page: String(page),
    };
    setSearchParams(newParams);
  };

  const filterPanel = (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Area</label>
          <Select
            value={pendingFilters.area}
            onValueChange={(value) => handlePendingFilterChange("area", value)}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select area" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Areas</SelectItem>
              {areaOptions.map((area) => (
                <SelectItem key={area.value} value={area.value}>
                  {area.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label
            className={`block text-sm font-medium mb-1 ${pendingFilters.area === "all" ? "text-gray-400" : "text-gray-700"
              }`}
          >
            Sub Area
          </label>
          <Select
            value={pendingFilters.subArea}
            onValueChange={(value) =>
              handlePendingFilterChange("subArea", value)
            }
            disabled={pendingFilters.area === "all"}
          >
            <SelectTrigger
              className={`w-full ${pendingFilters.area === "all"
                  ? "bg-gray-100 border-gray-300 cursor-not-allowed text-gray-400 opacity-60"
                  : "bg-white"
                }`}
            >
              <SelectValue placeholder="Select sub area" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sub Areas</SelectItem>
              {subAreaOptions.map((subArea) => (
                <SelectItem key={subArea.value} value={subArea.value}>
                  {subArea.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 mt-4">
        <Button variant="default" onClick={handleApplyFilters}>
          Apply Filters
        </Button>
        <Button variant="ghost" onClick={handleResetFilters}>
          Clear
        </Button>
      </div>
    </div>
  );

  // Define table columns
  const columns: TableColumn<any>[] = [
    {
      key: "select",
      header: (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) => handleSelectAll(!!checked)}
          />
        </div>
      ),
      width: "w-5",
      type: "custom",
      sortable: false,
      render: (_, row: RouteData) => (
        <div className="flex items-center justify-center">
          <Checkbox
            checked={selectedRoutes.includes(row.id)}
            onCheckedChange={(checked) => handleSelectRoute(row.id, !!checked)}
          />
        </div>
      ),
    },
    {
      key: "name",
      header: "Premise",
      headerTooltip:
        "Building or location assigned to this route. Click header to sort.",
    },
    {
      key: "area",
      header: "Area",
      headerTooltip:
        "Geographic zone for route planning. Filter to focus on specific regions.",
    },
    {
      key: "subArea",
      header: "Subarea",
      headerTooltip:
        "Subdivision within area for granular workload distribution.",
    },
    {
      key: "meters",
      header: "Resource",
      headerTooltip:
        "Number of meters at this premise. Higher counts indicate larger workload.",
    },
    {
      key: "consumers",
      header: "Consumers",
      headerTooltip:
        "Number of service accounts at this premise. Used for billing and customer service.",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <PageHeader
            title="Premises"
            subtitle="Search, filter by area/subarea, and review assigned premises."
          />
        </div>
        {selectedRoutes.length > 0 && (
          <div className="flex justify-end items-center gap-2">
            <Badge variant="outline">{selectedRoutes.length} routes</Badge>
            <Button onClick={handleOpenMoveRoutesDialog}>
              Move Premises to Different Route
            </Button>
          </div>
        )}
      </div>
      <div className="space-y-4">
        <GenericTable
          data={premiseDetail}
          columns={columns}
          rowKey="id"
          emptyMessage="No premises found matching your criteria"
          pagination={pagination}
          onPageChange={handlePageChange}
          onSearch={handleSearch}
          searchPlaceholder="Search by premise name or address"
          filterPanel={filterPanel}
        />
      </div>
      <MoveRoutesDialog
        open={moveRoutesDialogOpen}
        onOpenChange={setMoveRoutesDialogOpen}
        selectedRoutes={selectedRoutes}
        onMoveRoutes={handleMoveRoutes}
        estimatedMeters={estimatedMeters}
        selectedPremises={selectedRoutes}
      />
    </div>
  );
};
